import { Locale } from "@/lib/i18n";

type SeoCopy = {
  questionHow: string;
  answerHow: string;
  questionFree: string;
  answerFree: string;
  questionSafe: string;
  answerSafe: string;
  questionFormats: string;
  questionInstall: string;
  answerInstall: string;
  pdfFormats: string;
  imageFormats: string;
  textFormats: string;
  otherFormats: string;
  overviewPdf: string;
  overviewImage: string;
  overviewText: string;
  overviewOther: string;
  useCasesPdf: string[];
  useCasesImage: string[];
  useCasesText: string[];
  useCasesOther: string[];
  toolWord: string;
  featureList: string[];
};

const seoCopy: Record<Locale, SeoCopy> = {
  en: {
    questionHow: "How do I use {tool}?",
    answerHow:
      "Upload your file or enter your text, adjust the available settings, click the action button, and then download or copy the result. {tool} runs directly in your browser for better privacy and speed.",
    questionFree: "Is {tool} free to use?",
    answerFree:
      "Yes. {tool} is free to use, requires no registration, and can be used as often as you need.",
    questionSafe: "Is my data safe when using {tool}?",
    answerSafe:
      "Yes. Most processing happens directly in your browser, which helps keep your files private and under your control.",
    questionFormats: "What formats does {tool} support?",
    questionInstall: "Do I need to install anything to use {tool}?",
    answerInstall:
      "No installation is needed. {tool} works directly in your web browser.",
    pdfFormats:
      "{tool} supports PDF files and, depending on the workflow, common formats such as JPG, PNG, WEBP, Word, Excel, or PowerPoint.",
    imageFormats:
      "{tool} supports common image formats such as JPG, PNG, WEBP, GIF, BMP, TIFF, and SVG depending on the workflow.",
    textFormats:
      "{tool} works with plain text, formatted text, code snippets, and other text-based content.",
    otherFormats:
      "{tool} supports standard input and output formats for this workflow. Check the tool page for the exact compatibility list.",
    overviewPdf:
      "{tool} helps handle PDF workflows online without extra software. It is useful for fast document tasks, repeat operations, and teams that need browser-based processing.",
    overviewImage:
      "{tool} helps optimize, convert, or prepare images directly in the browser. It is useful for creators, ecommerce teams, and site owners who need quick results.",
    overviewText:
      "{tool} helps clean, transform, or analyze text directly in the browser. It is useful for writers, students, marketers, and developers.",
    overviewOther:
      "{tool} is a focused browser-based utility built to complete one workflow quickly with minimal friction.",
    useCasesPdf: [
      "Prepare contracts, reports, worksheets, and client files faster.",
      "Complete document tasks without installing desktop software.",
      "Support students, operations teams, and freelancers with lightweight PDF workflows.",
    ],
    useCasesImage: [
      "Prepare assets for websites, marketplaces, portfolios, and social posts.",
      "Convert or optimize files before uploading them to CMS or ecommerce systems.",
      "Reduce manual editing time for design and marketing workflows.",
    ],
    useCasesText: [
      "Format content for publishing, sharing, or collaboration.",
      "Transform structured text for docs, development, or content operations.",
      "Speed up repetitive writing and editing work.",
    ],
    useCasesOther: [
      "Complete the task directly in the browser.",
      "Avoid unnecessary setup or extra software.",
      "Move faster with a focused workflow.",
    ],
    toolWord: "Tool",
    featureList: [
      "Free to use",
      "No registration required",
      "Privacy-focused",
      "Fast processing",
      "Works in browser",
    ],
  },
  id: {
    questionHow: "Bagaimana cara menggunakan {tool}?",
    answerHow:
      "Upload file atau masukkan teks Anda, atur opsi yang tersedia, klik tombol aksi, lalu unduh atau salin hasilnya. {tool} berjalan langsung di browser untuk privasi dan kecepatan yang lebih baik.",
    questionFree: "Apakah {tool} gratis digunakan?",
    answerFree:
      "Ya. {tool} gratis digunakan, tidak perlu registrasi, dan bisa dipakai sesering yang Anda butuhkan.",
    questionSafe: "Apakah data saya aman saat menggunakan {tool}?",
    answerSafe:
      "Ya. Sebagian besar proses berjalan langsung di browser Anda, sehingga file tetap lebih privat dan berada dalam kendali Anda.",
    questionFormats: "Format apa saja yang didukung {tool}?",
    questionInstall: "Apakah saya perlu menginstal sesuatu untuk menggunakan {tool}?",
    answerInstall:
      "Tidak perlu instalasi. {tool} bisa digunakan langsung di browser web Anda.",
    pdfFormats:
      "{tool} mendukung file PDF dan, tergantung workflow-nya, format umum seperti JPG, PNG, WEBP, Word, Excel, atau PowerPoint.",
    imageFormats:
      "{tool} mendukung format gambar umum seperti JPG, PNG, WEBP, GIF, BMP, TIFF, dan SVG tergantung workflow-nya.",
    textFormats:
      "{tool} bekerja dengan teks biasa, teks berformat, potongan kode, dan konten berbasis teks lainnya.",
    otherFormats:
      "{tool} mendukung format input dan output standar untuk workflow ini. Lihat halaman tool untuk daftar kompatibilitas yang lebih rinci.",
    overviewPdf:
      "{tool} membantu menangani workflow PDF secara online tanpa software tambahan. Tool ini cocok untuk tugas dokumen cepat, pekerjaan berulang, dan tim yang membutuhkan proses berbasis browser.",
    overviewImage:
      "{tool} membantu mengoptimalkan, mengonversi, atau menyiapkan gambar langsung di browser. Cocok untuk kreator, tim ecommerce, dan pemilik website yang membutuhkan hasil cepat.",
    overviewText:
      "{tool} membantu membersihkan, mengubah, atau menganalisis teks langsung di browser. Cocok untuk penulis, pelajar, marketer, dan developer.",
    overviewOther:
      "{tool} adalah utilitas berbasis browser yang fokus menyelesaikan satu workflow dengan cepat dan minim hambatan.",
    useCasesPdf: [
      "Menyiapkan kontrak, laporan, worksheet, dan file klien dengan lebih cepat.",
      "Menyelesaikan tugas dokumen tanpa perlu software desktop.",
      "Membantu pelajar, tim operasional, dan freelancer dengan workflow PDF yang ringan.",
    ],
    useCasesImage: [
      "Menyiapkan aset untuk website, marketplace, portofolio, dan postingan sosial media.",
      "Mengonversi atau mengoptimalkan file sebelum diunggah ke CMS atau sistem ecommerce.",
      "Mengurangi waktu editing manual untuk workflow desain dan marketing.",
    ],
    useCasesText: [
      "Memformat konten untuk publikasi, berbagi, atau kolaborasi.",
      "Mengubah teks terstruktur untuk dokumentasi, development, atau content ops.",
      "Mempercepat pekerjaan menulis dan editing yang berulang.",
    ],
    useCasesOther: [
      "Selesaikan tugas langsung di browser.",
      "Hindari setup yang tidak perlu atau software tambahan.",
      "Bekerja lebih cepat dengan workflow yang fokus.",
    ],
    toolWord: "Tool",
    featureList: [
      "Gratis digunakan",
      "Tanpa registrasi",
      "Fokus pada privasi",
      "Proses cepat",
      "Berjalan di browser",
    ],
  },
  vi: {
    questionHow: "Cách sử dụng {tool} là gì?",
    answerHow:
      "Tải tệp lên hoặc nhập văn bản, chỉnh các tùy chọn khả dụng, bấm nút thao tác rồi tải xuống hoặc sao chép kết quả. {tool} chạy trực tiếp trong trình duyệt để tăng tốc độ và quyền riêng tư.",
    questionFree: "{tool} có miễn phí không?",
    answerFree:
      "Có. {tool} miễn phí, không cần đăng ký và bạn có thể dùng bao nhiêu lần tùy ý.",
    questionSafe: "Dữ liệu của tôi có an toàn khi dùng {tool} không?",
    answerSafe:
      "Có. Phần lớn quá trình xử lý diễn ra ngay trong trình duyệt nên tệp của bạn luôn được kiểm soát tốt hơn.",
    questionFormats: "{tool} hỗ trợ những định dạng nào?",
    questionInstall: "Tôi có cần cài gì để dùng {tool} không?",
    answerInstall: "Không cần cài đặt. {tool} hoạt động trực tiếp trong trình duyệt web.",
    pdfFormats:
      "{tool} hỗ trợ tệp PDF và, tùy workflow, các định dạng phổ biến như JPG, PNG, WEBP, Word, Excel hoặc PowerPoint.",
    imageFormats:
      "{tool} hỗ trợ các định dạng ảnh phổ biến như JPG, PNG, WEBP, GIF, BMP, TIFF và SVG tùy workflow.",
    textFormats:
      "{tool} làm việc với văn bản thuần, văn bản có định dạng, đoạn mã và nội dung dựa trên văn bản.",
    otherFormats:
      "{tool} hỗ trợ các định dạng đầu vào và đầu ra tiêu chuẩn cho workflow này. Xem trang công cụ để biết danh sách tương thích chi tiết.",
    overviewPdf:
      "{tool} giúp xử lý các tác vụ PDF trực tuyến mà không cần phần mềm bổ sung. Phù hợp cho công việc tài liệu nhanh và xử lý trên trình duyệt.",
    overviewImage:
      "{tool} giúp tối ưu, chuyển đổi hoặc chuẩn bị hình ảnh trực tiếp trong trình duyệt. Phù hợp cho nhà sáng tạo, đội ecommerce và chủ website.",
    overviewText:
      "{tool} giúp làm sạch, chuyển đổi hoặc phân tích văn bản trực tiếp trong trình duyệt. Phù hợp cho người viết, sinh viên, marketer và lập trình viên.",
    overviewOther:
      "{tool} là tiện ích tập trung trên trình duyệt để hoàn thành nhanh một workflow cụ thể.",
    useCasesPdf: [
      "Chuẩn bị hợp đồng, báo cáo, bảng tính và tệp khách hàng nhanh hơn.",
      "Hoàn thành tác vụ tài liệu mà không cần phần mềm desktop.",
      "Hỗ trợ sinh viên, nhóm vận hành và freelancer với workflow PDF gọn nhẹ.",
    ],
    useCasesImage: [
      "Chuẩn bị tài sản cho website, sàn thương mại điện tử, portfolio và mạng xã hội.",
      "Chuyển đổi hoặc tối ưu tệp trước khi tải lên CMS hoặc nền tảng ecommerce.",
      "Giảm thao tác chỉnh sửa thủ công cho quy trình thiết kế và marketing.",
    ],
    useCasesText: [
      "Định dạng nội dung để xuất bản, chia sẻ hoặc cộng tác.",
      "Chuyển đổi văn bản có cấu trúc cho tài liệu, phát triển hoặc content ops.",
      "Tăng tốc công việc viết và chỉnh sửa lặp lại.",
    ],
    useCasesOther: [
      "Hoàn thành tác vụ trực tiếp trong trình duyệt.",
      "Tránh cài đặt hoặc phần mềm không cần thiết.",
      "Làm việc nhanh hơn với workflow tập trung.",
    ],
    toolWord: "Công cụ",
    featureList: [
      "Miễn phí sử dụng",
      "Không cần đăng ký",
      "Tập trung vào quyền riêng tư",
      "Xử lý nhanh",
      "Chạy trong trình duyệt",
    ],
  },
  cs: {
    questionHow: "Jak používat {tool}?",
    answerHow:
      "Nahrajte soubor nebo vložte text, upravte dostupná nastavení, klikněte na tlačítko akce a pak výsledek stáhněte nebo zkopírujte. {tool} běží přímo v prohlížeči pro lepší soukromí a rychlost.",
    questionFree: "Je {tool} zdarma?",
    answerFree:
      "Ano. {tool} je zdarma, nevyžaduje registraci a můžete ho používat neomezeně.",
    questionSafe: "Jsou moje data při použití {tool} v bezpečí?",
    answerSafe:
      "Ano. Většina zpracování probíhá přímo ve vašem prohlížeči, takže soubory zůstávají více pod vaší kontrolou.",
    questionFormats: "Jaké formáty {tool} podporuje?",
    questionInstall: "Musím něco instalovat, abych mohl používat {tool}?",
    answerInstall: "Není potřeba nic instalovat. {tool} funguje přímo ve webovém prohlížeči.",
    pdfFormats:
      "{tool} podporuje PDF a podle workflow také běžné formáty jako JPG, PNG, WEBP, Word, Excel nebo PowerPoint.",
    imageFormats:
      "{tool} podporuje běžné obrazové formáty jako JPG, PNG, WEBP, GIF, BMP, TIFF a SVG podle workflow.",
    textFormats:
      "{tool} pracuje s prostým textem, formátovaným textem, úryvky kódu a dalším textovým obsahem.",
    otherFormats:
      "{tool} podporuje standardní vstupní a výstupní formáty pro tento workflow. Přesný seznam najdete na stránce nástroje.",
    overviewPdf:
      "{tool} pomáhá zpracovávat PDF online bez dalšího softwaru. Hodí se pro rychlé dokumentové úkoly a práci v prohlížeči.",
    overviewImage:
      "{tool} pomáhá optimalizovat, převádět nebo připravovat obrázky přímo v prohlížeči. Hodí se pro tvůrce, ecommerce týmy a správce webů.",
    overviewText:
      "{tool} pomáhá čistit, převádět nebo analyzovat text přímo v prohlížeči. Hodí se pro autory, studenty, marketéry i vývojáře.",
    overviewOther:
      "{tool} je cílený nástroj v prohlížeči pro rychlé dokončení jednoho workflow.",
    useCasesPdf: [
      "Rychleji připravit smlouvy, reporty, pracovní listy a klientské soubory.",
      "Vyřešit dokumentové úkoly bez desktopového softwaru.",
      "Podpořit studenty, operativní týmy a freelancery lehkým PDF workflow.",
    ],
    useCasesImage: [
      "Připravit podklady pro weby, marketplace, portfolia a sociální sítě.",
      "Převést nebo optimalizovat soubory před nahráním do CMS nebo ecommerce systému.",
      "Omezit ruční úpravy v designu a marketingu.",
    ],
    useCasesText: [
      "Formátovat obsah pro publikaci, sdílení nebo spolupráci.",
      "Převádět strukturovaný text pro dokumentaci, vývoj nebo content ops.",
      "Zrychlit opakující se psaní a editaci.",
    ],
    useCasesOther: [
      "Dokončete úkol přímo v prohlížeči.",
      "Vyhněte se zbytečnému nastavení nebo softwaru.",
      "Pracujte rychleji s cíleným workflow.",
    ],
    toolWord: "Nástroj",
    featureList: [
      "Zdarma",
      "Bez registrace",
      "Se zaměřením na soukromí",
      "Rychlé zpracování",
      "Funguje v prohlížeči",
    ],
  },
  es: {
    questionHow: "¿Cómo uso {tool}?",
    answerHow:
      "Sube tu archivo o escribe tu texto, ajusta las opciones disponibles, haz clic en el botón de acción y luego descarga o copia el resultado. {tool} funciona directamente en el navegador para ofrecer más privacidad y velocidad.",
    questionFree: "¿{tool} es gratis?",
    answerFree:
      "Sí. {tool} es gratuito, no requiere registro y puedes usarlo todas las veces que necesites.",
    questionSafe: "¿Mis datos están seguros al usar {tool}?",
    answerSafe:
      "Sí. La mayor parte del procesamiento ocurre directamente en tu navegador, lo que ayuda a mantener tus archivos bajo tu control.",
    questionFormats: "¿Qué formatos admite {tool}?",
    questionInstall: "¿Necesito instalar algo para usar {tool}?",
    answerInstall:
      "No necesitas instalar nada. {tool} funciona directamente en tu navegador web.",
    pdfFormats:
      "{tool} admite archivos PDF y, según el flujo, formatos comunes como JPG, PNG, WEBP, Word, Excel o PowerPoint.",
    imageFormats:
      "{tool} admite formatos de imagen comunes como JPG, PNG, WEBP, GIF, BMP, TIFF y SVG según el flujo.",
    textFormats:
      "{tool} funciona con texto plano, texto con formato, fragmentos de código y otro contenido basado en texto.",
    otherFormats:
      "{tool} admite formatos estándar de entrada y salida para este flujo de trabajo. Consulta la página de la herramienta para ver la compatibilidad exacta.",
    overviewPdf:
      "{tool} ayuda a resolver tareas PDF en línea sin software adicional. Es útil para trabajos rápidos con documentos y procesos basados en navegador.",
    overviewImage:
      "{tool} ayuda a optimizar, convertir o preparar imágenes directamente en el navegador. Es útil para creadores, equipos de ecommerce y propietarios de sitios web.",
    overviewText:
      "{tool} ayuda a limpiar, transformar o analizar texto directamente en el navegador. Es útil para redactores, estudiantes, marketers y desarrolladores.",
    overviewOther:
      "{tool} es una utilidad enfocada en navegador creada para completar un flujo específico rápidamente.",
    useCasesPdf: [
      "Preparar contratos, informes, hojas de trabajo y archivos de clientes con mayor rapidez.",
      "Resolver tareas documentales sin instalar software de escritorio.",
      "Ayudar a estudiantes, equipos de operaciones y freelancers con flujos PDF ligeros.",
    ],
    useCasesImage: [
      "Preparar recursos para sitios web, marketplaces, portafolios y redes sociales.",
      "Convertir u optimizar archivos antes de subirlos a un CMS o sistema ecommerce.",
      "Reducir edición manual en flujos de diseño y marketing.",
    ],
    useCasesText: [
      "Dar formato al contenido para publicar, compartir o colaborar.",
      "Transformar texto estructurado para documentación, desarrollo o content ops.",
      "Acelerar tareas repetitivas de escritura y edición.",
    ],
    useCasesOther: [
      "Completa la tarea directamente en el navegador.",
      "Evita configuraciones o software innecesarios.",
      "Avanza más rápido con un flujo enfocado.",
    ],
    toolWord: "Herramienta",
    featureList: [
      "Gratis",
      "Sin registro",
      "Enfoque en privacidad",
      "Procesamiento rápido",
      "Funciona en el navegador",
    ],
  },
  fr: {
    questionHow: "Comment utiliser {tool} ?",
    answerHow:
      "Téléversez votre fichier ou saisissez votre texte, ajustez les options disponibles, cliquez sur le bouton d'action puis téléchargez ou copiez le résultat. {tool} fonctionne directement dans le navigateur pour plus de confidentialité et de rapidité.",
    questionFree: "{tool} est-il gratuit ?",
    answerFree:
      "Oui. {tool} est gratuit, ne demande aucune inscription et peut être utilisé aussi souvent que nécessaire.",
    questionSafe: "Mes données sont-elles en sécurité avec {tool} ?",
    answerSafe:
      "Oui. La majeure partie du traitement se fait directement dans votre navigateur, ce qui aide à garder vos fichiers sous votre contrôle.",
    questionFormats: "Quels formats sont pris en charge par {tool} ?",
    questionInstall: "Dois-je installer quelque chose pour utiliser {tool} ?",
    answerInstall:
      "Aucune installation n'est nécessaire. {tool} fonctionne directement dans votre navigateur web.",
    pdfFormats:
      "{tool} prend en charge les fichiers PDF et, selon le workflow, des formats courants comme JPG, PNG, WEBP, Word, Excel ou PowerPoint.",
    imageFormats:
      "{tool} prend en charge des formats d'image courants comme JPG, PNG, WEBP, GIF, BMP, TIFF et SVG selon le workflow.",
    textFormats:
      "{tool} fonctionne avec du texte brut, du texte formaté, des extraits de code et d'autres contenus textuels.",
    otherFormats:
      "{tool} prend en charge les formats d'entrée et de sortie standards pour ce workflow. Consultez la page de l'outil pour la compatibilité exacte.",
    overviewPdf:
      "{tool} aide à gérer des workflows PDF en ligne sans logiciel supplémentaire. Il convient aux tâches documentaires rapides et au traitement dans le navigateur.",
    overviewImage:
      "{tool} aide à optimiser, convertir ou préparer des images directement dans le navigateur. Il convient aux créateurs, aux équipes ecommerce et aux propriétaires de sites.",
    overviewText:
      "{tool} aide à nettoyer, transformer ou analyser du texte directement dans le navigateur. Il convient aux rédacteurs, étudiants, marketeurs et développeurs.",
    overviewOther:
      "{tool} est un utilitaire ciblé dans le navigateur conçu pour terminer rapidement un workflow précis.",
    useCasesPdf: [
      "Préparer plus vite des contrats, rapports, feuilles de calcul et fichiers clients.",
      "Gérer des tâches documentaires sans logiciel de bureau.",
      "Aider étudiants, équipes opérationnelles et freelances avec des workflows PDF légers.",
    ],
    useCasesImage: [
      "Préparer des ressources pour des sites, marketplaces, portfolios et réseaux sociaux.",
      "Convertir ou optimiser des fichiers avant leur mise en ligne dans un CMS ou une plateforme ecommerce.",
      "Réduire les retouches manuelles dans les workflows design et marketing.",
    ],
    useCasesText: [
      "Mettre en forme du contenu pour publier, partager ou collaborer.",
      "Transformer du texte structuré pour la documentation, le développement ou les content ops.",
      "Accélérer l'écriture et l'édition répétitives.",
    ],
    useCasesOther: [
      "Effectuez la tâche directement dans le navigateur.",
      "Évitez les installations ou logiciels inutiles.",
      "Travaillez plus vite avec un workflow ciblé.",
    ],
    toolWord: "Outil",
    featureList: [
      "Gratuit",
      "Sans inscription",
      "Axé sur la confidentialité",
      "Traitement rapide",
      "Fonctionne dans le navigateur",
    ],
  },
  de: {
    questionHow: "Wie nutze ich {tool}?",
    answerHow:
      "Lade deine Datei hoch oder gib deinen Text ein, passe die verfügbaren Optionen an, klicke auf die Aktionsschaltfläche und lade das Ergebnis herunter oder kopiere es. {tool} läuft direkt im Browser für mehr Datenschutz und Geschwindigkeit.",
    questionFree: "Ist {tool} kostenlos?",
    answerFree:
      "Ja. {tool} ist kostenlos, erfordert keine Registrierung und kann beliebig oft genutzt werden.",
    questionSafe: "Sind meine Daten bei der Nutzung von {tool} sicher?",
    answerSafe:
      "Ja. Der Großteil der Verarbeitung findet direkt im Browser statt, sodass deine Dateien besser unter deiner Kontrolle bleiben.",
    questionFormats: "Welche Formate unterstützt {tool}?",
    questionInstall: "Muss ich etwas installieren, um {tool} zu nutzen?",
    answerInstall:
      "Es ist keine Installation nötig. {tool} funktioniert direkt in deinem Webbrowser.",
    pdfFormats:
      "{tool} unterstützt PDF-Dateien und je nach Workflow gängige Formate wie JPG, PNG, WEBP, Word, Excel oder PowerPoint.",
    imageFormats:
      "{tool} unterstützt je nach Workflow gängige Bildformate wie JPG, PNG, WEBP, GIF, BMP, TIFF und SVG.",
    textFormats:
      "{tool} arbeitet mit Klartext, formatiertem Text, Code-Snippets und anderen textbasierten Inhalten.",
    otherFormats:
      "{tool} unterstützt Standard-Eingabe- und Ausgabeformate für diesen Workflow. Die genaue Kompatibilität findest du auf der Tool-Seite.",
    overviewPdf:
      "{tool} hilft bei PDF-Workflows online ohne zusätzliche Software. Es eignet sich für schnelle Dokumentaufgaben und browserbasierte Verarbeitung.",
    overviewImage:
      "{tool} hilft beim Optimieren, Konvertieren oder Vorbereiten von Bildern direkt im Browser. Es eignet sich für Creator, Ecommerce-Teams und Website-Betreiber.",
    overviewText:
      "{tool} hilft beim Bereinigen, Umwandeln oder Analysieren von Text direkt im Browser. Es eignet sich für Autoren, Studenten, Marketer und Entwickler.",
    overviewOther:
      "{tool} ist ein fokussiertes Browser-Utility, das einen bestimmten Workflow schnell erledigt.",
    useCasesPdf: [
      "Verträge, Berichte, Arbeitsblätter und Kundendateien schneller vorbereiten.",
      "Dokumentaufgaben ohne Desktop-Software erledigen.",
      "Studenten, Ops-Teams und Freelancer mit leichtgewichtigen PDF-Workflows unterstützen.",
    ],
    useCasesImage: [
      "Assets für Websites, Marktplätze, Portfolios und Social Posts vorbereiten.",
      "Dateien vor dem Upload in CMS oder Ecommerce-Systeme konvertieren oder optimieren.",
      "Manuelle Bearbeitung in Design- und Marketing-Workflows reduzieren.",
    ],
    useCasesText: [
      "Inhalte für Veröffentlichung, Austausch oder Zusammenarbeit formatieren.",
      "Strukturierten Text für Doku, Entwicklung oder Content Ops umwandeln.",
      "Wiederkehrende Schreib- und Bearbeitungsarbeiten beschleunigen.",
    ],
    useCasesOther: [
      "Die Aufgabe direkt im Browser erledigen.",
      "Unnötige Einrichtung oder Software vermeiden.",
      "Mit einem fokussierten Workflow schneller arbeiten.",
    ],
    toolWord: "Werkzeug",
    featureList: [
      "Kostenlos nutzbar",
      "Keine Registrierung",
      "Datenschutzorientiert",
      "Schnelle Verarbeitung",
      "Läuft im Browser",
    ],
  },
  it: {
    questionHow: "Come si usa {tool}?",
    answerHow:
      "Carica il file o inserisci il testo, regola le opzioni disponibili, fai clic sul pulsante di azione e poi scarica o copia il risultato. {tool} funziona direttamente nel browser per più privacy e velocità.",
    questionFree: "{tool} è gratuito?",
    answerFree:
      "Sì. {tool} è gratuito, non richiede registrazione e puoi usarlo tutte le volte che vuoi.",
    questionSafe: "I miei dati sono al sicuro usando {tool}?",
    answerSafe:
      "Sì. La maggior parte dell'elaborazione avviene direttamente nel browser, aiutando a mantenere i file sotto il tuo controllo.",
    questionFormats: "Quali formati supporta {tool}?",
    questionInstall: "Devo installare qualcosa per usare {tool}?",
    answerInstall:
      "Non serve installare nulla. {tool} funziona direttamente nel browser web.",
    pdfFormats:
      "{tool} supporta i file PDF e, in base al workflow, formati comuni come JPG, PNG, WEBP, Word, Excel o PowerPoint.",
    imageFormats:
      "{tool} supporta formati immagine comuni come JPG, PNG, WEBP, GIF, BMP, TIFF e SVG a seconda del workflow.",
    textFormats:
      "{tool} funziona con testo semplice, testo formattato, snippet di codice e altri contenuti testuali.",
    otherFormats:
      "{tool} supporta i formati standard di input e output per questo workflow. Controlla la pagina dello strumento per la compatibilità esatta.",
    overviewPdf:
      "{tool} aiuta a gestire workflow PDF online senza software aggiuntivo. È utile per attività documentali rapide e processi nel browser.",
    overviewImage:
      "{tool} aiuta a ottimizzare, convertire o preparare immagini direttamente nel browser. È utile per creator, team ecommerce e proprietari di siti.",
    overviewText:
      "{tool} aiuta a pulire, trasformare o analizzare testo direttamente nel browser. È utile per scrittori, studenti, marketer e sviluppatori.",
    overviewOther:
      "{tool} è un'utilità mirata nel browser pensata per completare rapidamente un workflow specifico.",
    useCasesPdf: [
      "Preparare più velocemente contratti, report, fogli di lavoro e file clienti.",
      "Gestire attività documentali senza software desktop.",
      "Supportare studenti, team operativi e freelance con workflow PDF leggeri.",
    ],
    useCasesImage: [
      "Preparare risorse per siti web, marketplace, portfolio e social.",
      "Convertire o ottimizzare file prima del caricamento su CMS o sistemi ecommerce.",
      "Ridurre il lavoro manuale nei workflow di design e marketing.",
    ],
    useCasesText: [
      "Formattare contenuti per pubblicazione, condivisione o collaborazione.",
      "Trasformare testo strutturato per documentazione, sviluppo o content ops.",
      "Velocizzare attività ripetitive di scrittura e modifica.",
    ],
    useCasesOther: [
      "Completa l'attività direttamente nel browser.",
      "Evita configurazioni o software non necessari.",
      "Lavora più velocemente con un workflow mirato.",
    ],
    toolWord: "Strumento",
    featureList: [
      "Gratuito",
      "Nessuna registrazione",
      "Attento alla privacy",
      "Elaborazione rapida",
      "Funziona nel browser",
    ],
  },
  hu: {
    questionHow: "Hogyan használható a(z) {tool}?",
    answerHow:
      "Töltsd fel a fájlt vagy írd be a szöveget, állítsd be az elérhető opciókat, kattints a művelet gombra, majd töltsd le vagy másold ki az eredményt. A(z) {tool} közvetlenül a böngészőben fut a jobb adatvédelem és sebesség érdekében.",
    questionFree: "A(z) {tool} ingyenes?",
    answerFree:
      "Igen. A(z) {tool} ingyenes, nem igényel regisztrációt, és annyiszor használható, ahányszor szükséges.",
    questionSafe: "Biztonságban vannak az adataim a(z) {tool} használata közben?",
    answerSafe:
      "Igen. A feldolgozás nagy része közvetlenül a böngészőben történik, így a fájlok jobban a te felügyeleted alatt maradnak.",
    questionFormats: "Milyen formátumokat támogat a(z) {tool}?",
    questionInstall: "Kell telepítenem valamit a(z) {tool} használatához?",
    answerInstall: "Nincs szükség telepítésre. A(z) {tool} közvetlenül a böngészőben működik.",
    pdfFormats:
      "A(z) {tool} támogatja a PDF-et és a workflow-tól függően az olyan gyakori formátumokat is, mint a JPG, PNG, WEBP, Word, Excel vagy PowerPoint.",
    imageFormats:
      "A(z) {tool} a workflow-tól függően támogatja a JPG, PNG, WEBP, GIF, BMP, TIFF és SVG formátumokat.",
    textFormats:
      "A(z) {tool} egyszerű szöveggel, formázott szöveggel, kódrészletekkel és más szöveges tartalommal működik.",
    otherFormats:
      "A(z) {tool} ehhez a workflow-hoz szabványos be- és kimeneti formátumokat támogat. A pontos kompatibilitást az eszköz oldalán találod.",
    overviewPdf:
      "A(z) {tool} segít a PDF workflow-k online kezelésében extra szoftver nélkül. Ideális gyors dokumentumfeladatokhoz és böngészőalapú feldolgozáshoz.",
    overviewImage:
      "A(z) {tool} segít képek optimalizálásában, konvertálásában vagy előkészítésében közvetlenül a böngészőben. Hasznos alkotóknak, ecommerce csapatoknak és weboldaltulajdonosoknak.",
    overviewText:
      "A(z) {tool} segít a szöveg tisztításában, átalakításában vagy elemzésében közvetlenül a böngészőben. Hasznos íróknak, diákoknak, marketingeseknek és fejlesztőknek.",
    overviewOther:
      "A(z) {tool} egy célzott böngészős segédprogram, amely egy konkrét workflow gyors elvégzésére készült.",
    useCasesPdf: [
      "Szerződések, jelentések, munkalapok és ügyfélfájlok gyorsabb előkészítése.",
      "Dokumentumfeladatok elvégzése asztali szoftver nélkül.",
      "Diákok, operatív csapatok és szabadúszók támogatása könnyű PDF workflow-kkal.",
    ],
    useCasesImage: [
      "Erőforrások előkészítése weboldalakhoz, piacterekhez, portfóliókhoz és közösségi posztokhoz.",
      "Fájlok konvertálása vagy optimalizálása CMS vagy ecommerce rendszerbe való feltöltés előtt.",
      "A manuális szerkesztés csökkentése design és marketing workflow-kban.",
    ],
    useCasesText: [
      "Tartalom formázása publikáláshoz, megosztáshoz vagy együttműködéshez.",
      "Strukturált szöveg átalakítása dokumentációhoz, fejlesztéshez vagy content ops-hoz.",
      "Az ismétlődő írási és szerkesztési munka gyorsítása.",
    ],
    useCasesOther: [
      "Végezd el a feladatot közvetlenül a böngészőben.",
      "Kerüld el a felesleges beállítást vagy szoftvert.",
      "Dolgozz gyorsabban egy fókuszált workflow-val.",
    ],
    toolWord: "Eszköz",
    featureList: [
      "Ingyenesen használható",
      "Nincs regisztráció",
      "Adatvédelem-központú",
      "Gyors feldolgozás",
      "Böngészőben működik",
    ],
  },
  ms: {
    questionHow: "Bagaimana cara menggunakan {tool}?",
    answerHow:
      "Muat naik fail anda atau masukkan teks, laraskan pilihan yang tersedia, klik butang tindakan, kemudian muat turun atau salin hasilnya. {tool} berjalan terus dalam pelayar untuk privasi dan kelajuan yang lebih baik.",
    questionFree: "Adakah {tool} percuma?",
    answerFree:
      "Ya. {tool} percuma digunakan, tidak memerlukan pendaftaran, dan boleh digunakan sekerap yang anda perlukan.",
    questionSafe: "Adakah data saya selamat apabila menggunakan {tool}?",
    answerSafe:
      "Ya. Kebanyakan pemprosesan berlaku terus dalam pelayar anda, jadi fail anda kekal lebih terkawal.",
    questionFormats: "Format apa yang disokong oleh {tool}?",
    questionInstall: "Perlukah saya memasang apa-apa untuk menggunakan {tool}?",
    answerInstall:
      "Tidak perlu pemasangan. {tool} berfungsi terus dalam pelayar web anda.",
    pdfFormats:
      "{tool} menyokong fail PDF dan, bergantung pada aliran kerja, format biasa seperti JPG, PNG, WEBP, Word, Excel atau PowerPoint.",
    imageFormats:
      "{tool} menyokong format imej biasa seperti JPG, PNG, WEBP, GIF, BMP, TIFF dan SVG bergantung pada aliran kerja.",
    textFormats:
      "{tool} berfungsi dengan teks biasa, teks berformat, cebisan kod dan kandungan berasaskan teks lain.",
    otherFormats:
      "{tool} menyokong format input dan output standard untuk aliran kerja ini. Semak halaman alat untuk keserasian tepat.",
    overviewPdf:
      "{tool} membantu mengurus aliran kerja PDF dalam talian tanpa perisian tambahan. Sesuai untuk tugasan dokumen pantas dan pemprosesan berasaskan pelayar.",
    overviewImage:
      "{tool} membantu mengoptimum, menukar atau menyediakan imej terus dalam pelayar. Sesuai untuk pencipta, pasukan ecommerce dan pemilik laman web.",
    overviewText:
      "{tool} membantu membersihkan, menukar atau menganalisis teks terus dalam pelayar. Sesuai untuk penulis, pelajar, pemasar dan pembangun.",
    overviewOther:
      "{tool} ialah utiliti berasaskan pelayar yang fokus untuk menyelesaikan satu aliran kerja dengan cepat.",
    useCasesPdf: [
      "Sediakan kontrak, laporan, lembaran kerja dan fail pelanggan dengan lebih pantas.",
      "Selesaikan tugasan dokumen tanpa perisian desktop.",
      "Menyokong pelajar, pasukan operasi dan freelancer dengan aliran kerja PDF yang ringan.",
    ],
    useCasesImage: [
      "Sediakan aset untuk laman web, marketplace, portfolio dan siaran sosial.",
      "Tukar atau optimumkan fail sebelum memuat naik ke CMS atau sistem ecommerce.",
      "Kurangkan kerja suntingan manual dalam aliran kerja reka bentuk dan pemasaran.",
    ],
    useCasesText: [
      "Format kandungan untuk penerbitan, perkongsian atau kerjasama.",
      "Tukar teks berstruktur untuk dokumentasi, pembangunan atau content ops.",
      "Percepat kerja menulis dan menyunting yang berulang.",
    ],
    useCasesOther: [
      "Selesaikan tugas terus dalam pelayar.",
      "Elakkan persediaan atau perisian yang tidak perlu.",
      "Bergerak lebih pantas dengan aliran kerja yang fokus.",
    ],
    toolWord: "Alat",
    featureList: [
      "Percuma digunakan",
      "Tiada pendaftaran",
      "Fokus privasi",
      "Pemprosesan pantas",
      "Berfungsi dalam pelayar",
    ],
  },
  nl: {
    questionHow: "Hoe gebruik ik {tool}?",
    answerHow:
      "Upload je bestand of voer je tekst in, pas de beschikbare instellingen aan, klik op de actieknop en download of kopieer daarna het resultaat. {tool} werkt direct in de browser voor meer privacy en snelheid.",
    questionFree: "Is {tool} gratis te gebruiken?",
    answerFree:
      "Ja. {tool} is gratis, vereist geen registratie en kan zo vaak worden gebruikt als nodig.",
    questionSafe: "Zijn mijn gegevens veilig bij het gebruik van {tool}?",
    answerSafe:
      "Ja. De meeste verwerking gebeurt direct in je browser, waardoor je bestanden beter onder jouw controle blijven.",
    questionFormats: "Welke formaten ondersteunt {tool}?",
    questionInstall: "Moet ik iets installeren om {tool} te gebruiken?",
    answerInstall:
      "Er is geen installatie nodig. {tool} werkt direct in je webbrowser.",
    pdfFormats:
      "{tool} ondersteunt PDF-bestanden en, afhankelijk van de workflow, gangbare formaten zoals JPG, PNG, WEBP, Word, Excel of PowerPoint.",
    imageFormats:
      "{tool} ondersteunt gangbare afbeeldingsformaten zoals JPG, PNG, WEBP, GIF, BMP, TIFF en SVG afhankelijk van de workflow.",
    textFormats:
      "{tool} werkt met platte tekst, opgemaakte tekst, codefragmenten en andere tekstgebaseerde inhoud.",
    otherFormats:
      "{tool} ondersteunt standaard invoer- en uitvoerformaten voor deze workflow. Bekijk de toolpagina voor de exacte compatibiliteit.",
    overviewPdf:
      "{tool} helpt PDF-workflows online af te handelen zonder extra software. Het is handig voor snelle documenttaken en browsergebaseerde verwerking.",
    overviewImage:
      "{tool} helpt afbeeldingen direct in de browser te optimaliseren, converteren of voorbereiden. Handig voor creators, ecommerce-teams en website-eigenaren.",
    overviewText:
      "{tool} helpt tekst direct in de browser schoon te maken, te transformeren of te analyseren. Handig voor schrijvers, studenten, marketeers en ontwikkelaars.",
    overviewOther:
      "{tool} is een gerichte browsertool om snel één specifieke workflow af te ronden.",
    useCasesPdf: [
      "Contracten, rapporten, werkbladen en klantbestanden sneller voorbereiden.",
      "Documenttaken afronden zonder desktopsoftware.",
      "Studenten, operationele teams en freelancers ondersteunen met lichte PDF-workflows.",
    ],
    useCasesImage: [
      "Assets voorbereiden voor websites, marketplaces, portfolio's en social posts.",
      "Bestanden converteren of optimaliseren voordat ze naar CMS of ecommerce-systemen worden geüpload.",
      "Handmatig bewerken in design- en marketingworkflows verminderen.",
    ],
    useCasesText: [
      "Content opmaken voor publicatie, delen of samenwerking.",
      "Gestructureerde tekst omzetten voor documentatie, development of content ops.",
      "Herhaald schrijf- en redactiewerk versnellen.",
    ],
    useCasesOther: [
      "Voer de taak direct in de browser uit.",
      "Vermijd onnodige installatie of software.",
      "Werk sneller met een gerichte workflow.",
    ],
    toolWord: "Tool",
    featureList: [
      "Gratis te gebruiken",
      "Geen registratie nodig",
      "Privacygericht",
      "Snelle verwerking",
      "Werkt in de browser",
    ],
  },
  pl: {
    questionHow: "Jak używać {tool}?",
    answerHow:
      "Prześlij plik lub wpisz tekst, dostosuj dostępne ustawienia, kliknij przycisk akcji, a następnie pobierz lub skopiuj wynik. {tool} działa bezpośrednio w przeglądarce dla lepszej prywatności i szybkości.",
    questionFree: "Czy {tool} jest darmowe?",
    answerFree:
      "Tak. {tool} jest darmowe, nie wymaga rejestracji i można z niego korzystać tyle razy, ile potrzeba.",
    questionSafe: "Czy moje dane są bezpieczne podczas używania {tool}?",
    answerSafe:
      "Tak. Większość przetwarzania odbywa się bezpośrednio w przeglądarce, dzięki czemu pliki pozostają bardziej pod Twoją kontrolą.",
    questionFormats: "Jakie formaty obsługuje {tool}?",
    questionInstall: "Czy muszę coś instalować, aby używać {tool}?",
    answerInstall:
      "Nie trzeba nic instalować. {tool} działa bezpośrednio w przeglądarce internetowej.",
    pdfFormats:
      "{tool} obsługuje pliki PDF oraz, zależnie od workflow, popularne formaty takie jak JPG, PNG, WEBP, Word, Excel lub PowerPoint.",
    imageFormats:
      "{tool} obsługuje popularne formaty obrazów, takie jak JPG, PNG, WEBP, GIF, BMP, TIFF i SVG zależnie od workflow.",
    textFormats:
      "{tool} działa z tekstem zwykłym, formatowanym, fragmentami kodu i inną treścią tekstową.",
    otherFormats:
      "{tool} obsługuje standardowe formaty wejścia i wyjścia dla tego workflow. Sprawdź stronę narzędzia, aby zobaczyć dokładną zgodność.",
    overviewPdf:
      "{tool} pomaga obsługiwać workflow PDF online bez dodatkowego oprogramowania. Przydaje się do szybkich zadań dokumentowych i pracy w przeglądarce.",
    overviewImage:
      "{tool} pomaga optymalizować, konwertować lub przygotowywać obrazy bezpośrednio w przeglądarce. Przydaje się twórcom, zespołom ecommerce i właścicielom stron.",
    overviewText:
      "{tool} pomaga czyścić, przekształcać lub analizować tekst bezpośrednio w przeglądarce. Przydaje się autorom, studentom, marketerom i programistom.",
    overviewOther:
      "{tool} to wyspecjalizowane narzędzie przeglądarkowe do szybkiego wykonania jednego konkretnego workflow.",
    useCasesPdf: [
      "Szybsze przygotowanie umów, raportów, arkuszy i plików klientów.",
      "Wykonywanie zadań dokumentowych bez oprogramowania desktopowego.",
      "Wsparcie studentów, zespołów operacyjnych i freelancerów lekkimi workflow PDF.",
    ],
    useCasesImage: [
      "Przygotowanie materiałów do stron internetowych, marketplace'ów, portfolio i social mediów.",
      "Konwersja lub optymalizacja plików przed przesłaniem do CMS lub systemów ecommerce.",
      "Ograniczenie ręcznej edycji w workflow projektowych i marketingowych.",
    ],
    useCasesText: [
      "Formatowanie treści do publikacji, udostępniania lub współpracy.",
      "Przekształcanie uporządkowanego tekstu do dokumentacji, developmentu lub content ops.",
      "Przyspieszenie powtarzalnej pracy nad pisaniem i edycją.",
    ],
    useCasesOther: [
      "Wykonaj zadanie bezpośrednio w przeglądarce.",
      "Unikaj zbędnej konfiguracji i dodatkowego oprogramowania.",
      "Pracuj szybciej dzięki skupionemu workflow.",
    ],
    toolWord: "Narzędzie",
    featureList: [
      "Darmowe w użyciu",
      "Bez rejestracji",
      "Nastawione na prywatność",
      "Szybkie przetwarzanie",
      "Działa w przeglądarce",
    ],
  },
  pt: {
    questionHow: "Como usar {tool}?",
    answerHow:
      "Envie seu arquivo ou digite seu texto, ajuste as opções disponíveis, clique no botão de ação e depois baixe ou copie o resultado. {tool} funciona diretamente no navegador para mais privacidade e velocidade.",
    questionFree: "{tool} é gratuito?",
    answerFree:
      "Sim. {tool} é gratuito, não exige cadastro e pode ser usado quantas vezes você precisar.",
    questionSafe: "Meus dados estão seguros ao usar {tool}?",
    answerSafe:
      "Sim. A maior parte do processamento acontece diretamente no navegador, ajudando a manter seus arquivos sob seu controle.",
    questionFormats: "Quais formatos o {tool} suporta?",
    questionInstall: "Preciso instalar algo para usar {tool}?",
    answerInstall:
      "Não é necessário instalar nada. {tool} funciona diretamente no navegador.",
    pdfFormats:
      "{tool} suporta arquivos PDF e, dependendo do fluxo, formatos comuns como JPG, PNG, WEBP, Word, Excel ou PowerPoint.",
    imageFormats:
      "{tool} suporta formatos de imagem comuns como JPG, PNG, WEBP, GIF, BMP, TIFF e SVG dependendo do fluxo.",
    textFormats:
      "{tool} funciona com texto simples, texto formatado, trechos de código e outros conteúdos baseados em texto.",
    otherFormats:
      "{tool} suporta formatos padrão de entrada e saída para este fluxo. Consulte a página da ferramenta para ver a compatibilidade exata.",
    overviewPdf:
      "{tool} ajuda a lidar com fluxos PDF online sem software extra. É útil para tarefas rápidas com documentos e processamento no navegador.",
    overviewImage:
      "{tool} ajuda a otimizar, converter ou preparar imagens diretamente no navegador. É útil para criadores, equipes de ecommerce e proprietários de sites.",
    overviewText:
      "{tool} ajuda a limpar, transformar ou analisar texto diretamente no navegador. É útil para escritores, estudantes, profissionais de marketing e desenvolvedores.",
    overviewOther:
      "{tool} é um utilitário focado no navegador criado para concluir rapidamente um workflow específico.",
    useCasesPdf: [
      "Preparar contratos, relatórios, planilhas e arquivos de clientes com mais rapidez.",
      "Concluir tarefas documentais sem software desktop.",
      "Apoiar estudantes, equipes operacionais e freelancers com fluxos PDF leves.",
    ],
    useCasesImage: [
      "Preparar arquivos para sites, marketplaces, portfólios e posts sociais.",
      "Converter ou otimizar arquivos antes de enviá-los a CMS ou sistemas de ecommerce.",
      "Reduzir edição manual em fluxos de design e marketing.",
    ],
    useCasesText: [
      "Formatar conteúdo para publicação, compartilhamento ou colaboração.",
      "Transformar texto estruturado para documentação, desenvolvimento ou content ops.",
      "Acelerar trabalho repetitivo de escrita e edição.",
    ],
    useCasesOther: [
      "Conclua a tarefa diretamente no navegador.",
      "Evite configuração ou software desnecessário.",
      "Avance mais rápido com um fluxo focado.",
    ],
    toolWord: "Ferramenta",
    featureList: [
      "Gratuito",
      "Sem cadastro",
      "Foco em privacidade",
      "Processamento rápido",
      "Funciona no navegador",
    ],
  },
  th: {
    questionHow: "ใช้งาน {tool} อย่างไร?",
    answerHow:
      "อัปโหลดไฟล์หรือใส่ข้อความของคุณ ปรับตัวเลือกที่มี กดปุ่มดำเนินการ แล้วดาวน์โหลดหรือคัดลอกผลลัพธ์ {tool} ทำงานได้โดยตรงในเบราว์เซอร์เพื่อความเป็นส่วนตัวและความเร็วที่ดีกว่า",
    questionFree: "{tool} ใช้งานฟรีหรือไม่?",
    answerFree:
      "ใช่ {tool} ใช้งานฟรี ไม่ต้องสมัครสมาชิก และใช้ได้บ่อยเท่าที่ต้องการ",
    questionSafe: "ข้อมูลของฉันปลอดภัยเมื่อใช้ {tool} หรือไม่?",
    answerSafe:
      "ใช่ การประมวลผลส่วนใหญ่เกิดขึ้นโดยตรงในเบราว์เซอร์ของคุณ จึงช่วยให้ไฟล์อยู่ภายใต้การควบคุมของคุณมากขึ้น",
    questionFormats: "{tool} รองรับไฟล์รูปแบบใดบ้าง?",
    questionInstall: "ฉันต้องติดตั้งอะไรเพื่อใช้ {tool} หรือไม่?",
    answerInstall: "ไม่ต้องติดตั้งอะไร {tool} ใช้งานได้โดยตรงในเว็บเบราว์เซอร์",
    pdfFormats:
      "{tool} รองรับไฟล์ PDF และตาม workflow ยังรองรับรูปแบบทั่วไปเช่น JPG, PNG, WEBP, Word, Excel หรือ PowerPoint",
    imageFormats:
      "{tool} รองรับไฟล์ภาพทั่วไป เช่น JPG, PNG, WEBP, GIF, BMP, TIFF และ SVG ตาม workflow",
    textFormats:
      "{tool} ใช้งานได้กับข้อความธรรมดา ข้อความที่มีรูปแบบ โค้ดสั้น ๆ และเนื้อหาที่เป็นข้อความอื่น ๆ",
    otherFormats:
      "{tool} รองรับรูปแบบอินพุตและเอาต์พุตมาตรฐานสำหรับ workflow นี้ ดูรายละเอียดความเข้ากันได้ในหน้าของเครื่องมือ",
    overviewPdf:
      "{tool} ช่วยจัดการงาน PDF ออนไลน์โดยไม่ต้องใช้ซอฟต์แวร์เพิ่มเติม เหมาะสำหรับงานเอกสารที่ต้องการความเร็วและการทำงานผ่านเบราว์เซอร์",
    overviewImage:
      "{tool} ช่วยปรับแต่ง แปลง หรือเตรียมภาพได้โดยตรงในเบราว์เซอร์ เหมาะสำหรับครีเอเตอร์ ทีม ecommerce และเจ้าของเว็บไซต์",
    overviewText:
      "{tool} ช่วยล้าง แปลง หรือวิเคราะห์ข้อความได้โดยตรงในเบราว์เซอร์ เหมาะสำหรับนักเขียน นักเรียน นักการตลาด และนักพัฒนา",
    overviewOther:
      "{tool} เป็นยูทิลิตีบนเบราว์เซอร์ที่เน้นการจบ workflow เฉพาะอย่างได้อย่างรวดเร็ว",
    useCasesPdf: [
      "เตรียมสัญญา รายงาน เวิร์กชีต และไฟล์ลูกค้าได้รวดเร็วขึ้น",
      "ทำงานเอกสารให้เสร็จโดยไม่ต้องใช้ซอฟต์แวร์เดสก์ท็อป",
      "ช่วยนักเรียน ทีมปฏิบัติการ และฟรีแลนซ์ด้วย workflow PDF ที่เบาและรวดเร็ว",
    ],
    useCasesImage: [
      "เตรียมไฟล์สำหรับเว็บไซต์ มาร์เก็ตเพลส พอร์ตโฟลิโอ และโพสต์โซเชียล",
      "แปลงหรือปรับภาพให้เหมาะสมก่อนอัปโหลดไปยัง CMS หรือระบบ ecommerce",
      "ลดขั้นตอนการแก้ไขด้วยมือในงานดีไซน์และการตลาด",
    ],
    useCasesText: [
      "จัดรูปแบบเนื้อหาสำหรับการเผยแพร่ การแชร์ หรือการทำงานร่วมกัน",
      "แปลงข้อความที่มีโครงสร้างสำหรับเอกสาร การพัฒนา หรือ content ops",
      "เร่งงานเขียนและแก้ไขที่ทำซ้ำบ่อย",
    ],
    useCasesOther: [
      "ทำงานให้เสร็จได้โดยตรงในเบราว์เซอร์",
      "หลีกเลี่ยงการติดตั้งหรือซอฟต์แวร์ที่ไม่จำเป็น",
      "ทำงานได้เร็วขึ้นด้วย workflow ที่ชัดเจน",
    ],
    toolWord: "เครื่องมือ",
    featureList: [
      "ใช้งานฟรี",
      "ไม่ต้องสมัครสมาชิก",
      "เน้นความเป็นส่วนตัว",
      "ประมวลผลรวดเร็ว",
      "ทำงานในเบราว์เซอร์",
    ],
  },
  tr: {
    questionHow: "{tool} nasıl kullanılır?",
    answerHow:
      "Dosyanızı yükleyin veya metninizi girin, mevcut seçenekleri ayarlayın, işlem düğmesine tıklayın ve ardından sonucu indirin ya da kopyalayın. {tool} daha iyi gizlilik ve hız için doğrudan tarayıcıda çalışır.",
    questionFree: "{tool} ücretsiz mi?",
    answerFree:
      "Evet. {tool} ücretsizdir, kayıt gerektirmez ve ihtiyaç duyduğunuz kadar kullanılabilir.",
    questionSafe: "{tool} kullanırken verilerim güvende mi?",
    answerSafe:
      "Evet. İşlemlerin çoğu doğrudan tarayıcınızda gerçekleşir, bu da dosyalarınızın daha fazla kontrolünüz altında kalmasına yardımcı olur.",
    questionFormats: "{tool} hangi formatları destekliyor?",
    questionInstall: "{tool} kullanmak için bir şey kurmam gerekir mi?",
    answerInstall: "Kurulum gerekmez. {tool} doğrudan web tarayıcınızda çalışır.",
    pdfFormats:
      "{tool} PDF dosyalarını ve iş akışına bağlı olarak JPG, PNG, WEBP, Word, Excel veya PowerPoint gibi yaygın formatları destekler.",
    imageFormats:
      "{tool} iş akışına bağlı olarak JPG, PNG, WEBP, GIF, BMP, TIFF ve SVG gibi yaygın görüntü formatlarını destekler.",
    textFormats:
      "{tool} düz metin, biçimlendirilmiş metin, kod parçaları ve diğer metin tabanlı içeriklerle çalışır.",
    otherFormats:
      "{tool} bu iş akışı için standart giriş ve çıkış formatlarını destekler. Tam uyumluluk için araç sayfasına bakın.",
    overviewPdf:
      "{tool} ek yazılım gerektirmeden PDF iş akışlarını çevrimiçi yönetmeye yardımcı olur. Hızlı belge işleri ve tarayıcı tabanlı işlem için uygundur.",
    overviewImage:
      "{tool} görüntüleri doğrudan tarayıcıda optimize etmeye, dönüştürmeye veya hazırlamaya yardımcı olur. İçerik üreticileri, ecommerce ekipleri ve site sahipleri için uygundur.",
    overviewText:
      "{tool} metni doğrudan tarayıcıda temizlemeye, dönüştürmeye veya analiz etmeye yardımcı olur. Yazarlar, öğrenciler, pazarlamacılar ve geliştiriciler için uygundur.",
    overviewOther:
      "{tool}, tek bir iş akışını hızlıca tamamlamak için tasarlanmış odaklı bir tarayıcı aracıdır.",
    useCasesPdf: [
      "Sözleşmeleri, raporları, çalışma sayfalarını ve müşteri dosyalarını daha hızlı hazırlamak.",
      "Masaüstü yazılım olmadan belge görevlerini tamamlamak.",
      "Öğrencileri, operasyon ekiplerini ve freelancer'ları hafif PDF iş akışlarıyla desteklemek.",
    ],
    useCasesImage: [
      "Web siteleri, pazaryerleri, portföyler ve sosyal içerikler için varlık hazırlamak.",
      "Dosyaları CMS veya ecommerce sistemlerine yüklemeden önce dönüştürmek ya da optimize etmek.",
      "Tasarım ve pazarlama iş akışlarında manuel düzenlemeyi azaltmak.",
    ],
    useCasesText: [
      "İçeriği yayınlama, paylaşma veya iş birliği için biçimlendirmek.",
      "Yapılandırılmış metni dokümantasyon, geliştirme veya content ops için dönüştürmek.",
      "Tekrarlayan yazma ve düzenleme işlerini hızlandırmak.",
    ],
    useCasesOther: [
      "Görevi doğrudan tarayıcıda tamamlayın.",
      "Gereksiz kurulum ve yazılımdan kaçının.",
      "Odaklı bir iş akışıyla daha hızlı ilerleyin.",
    ],
    toolWord: "Araç",
    featureList: [
      "Ücretsiz kullanım",
      "Kayıt gerekmez",
      "Gizlilik odaklı",
      "Hızlı işlem",
      "Tarayıcıda çalışır",
    ],
  },
  el: {
    questionHow: "Πώς χρησιμοποιώ το {tool};",
    answerHow:
      "Ανεβάστε το αρχείο σας ή εισαγάγετε το κείμενό σας, προσαρμόστε τις διαθέσιμες επιλογές, πατήστε το κουμπί ενέργειας και έπειτα κατεβάστε ή αντιγράψτε το αποτέλεσμα. Το {tool} λειτουργεί απευθείας στον browser για καλύτερη ιδιωτικότητα και ταχύτητα.",
    questionFree: "Είναι δωρεάν το {tool};",
    answerFree:
      "Ναι. Το {tool} είναι δωρεάν, δεν απαιτεί εγγραφή και μπορεί να χρησιμοποιηθεί όσες φορές χρειάζεστε.",
    questionSafe: "Είναι ασφαλή τα δεδομένα μου όταν χρησιμοποιώ το {tool};",
    answerSafe:
      "Ναι. Το μεγαλύτερο μέρος της επεξεργασίας γίνεται απευθείας στον browser σας, ώστε τα αρχεία να παραμένουν περισσότερο υπό τον έλεγχό σας.",
    questionFormats: "Ποιες μορφές υποστηρίζει το {tool};",
    questionInstall: "Χρειάζεται να εγκαταστήσω κάτι για να χρησιμοποιήσω το {tool};",
    answerInstall:
      "Δεν απαιτείται εγκατάσταση. Το {tool} λειτουργεί απευθείας στον web browser σας.",
    pdfFormats:
      "Το {tool} υποστηρίζει αρχεία PDF και, ανάλογα με το workflow, συνηθισμένες μορφές όπως JPG, PNG, WEBP, Word, Excel ή PowerPoint.",
    imageFormats:
      "Το {tool} υποστηρίζει συνήθεις μορφές εικόνας όπως JPG, PNG, WEBP, GIF, BMP, TIFF και SVG ανάλογα με το workflow.",
    textFormats:
      "Το {tool} λειτουργεί με απλό κείμενο, μορφοποιημένο κείμενο, αποσπάσματα κώδικα και άλλο περιεχόμενο βασισμένο σε κείμενο.",
    otherFormats:
      "Το {tool} υποστηρίζει τυπικές μορφές εισόδου και εξόδου για αυτό το workflow. Δείτε τη σελίδα του εργαλείου για ακριβή συμβατότητα.",
    overviewPdf:
      "Το {tool} βοηθά στη διαχείριση PDF workflows online χωρίς επιπλέον λογισμικό. Είναι χρήσιμο για γρήγορες εργασίες εγγράφων και επεξεργασία μέσα από τον browser.",
    overviewImage:
      "Το {tool} βοηθά στη βελτιστοποίηση, μετατροπή ή προετοιμασία εικόνων απευθείας στον browser. Είναι χρήσιμο για creators, ομάδες ecommerce και ιδιοκτήτες ιστοσελίδων.",
    overviewText:
      "Το {tool} βοηθά στον καθαρισμό, τη μετατροπή ή την ανάλυση κειμένου απευθείας στον browser. Είναι χρήσιμο για συγγραφείς, φοιτητές, marketers και developers.",
    overviewOther:
      "Το {tool} είναι ένα στοχευμένο εργαλείο browser σχεδιασμένο να ολοκληρώνει γρήγορα ένα συγκεκριμένο workflow.",
    useCasesPdf: [
      "Προετοιμασία συμβολαίων, αναφορών, φύλλων εργασίας και αρχείων πελατών πιο γρήγορα.",
      "Ολοκλήρωση εργασιών εγγράφων χωρίς desktop λογισμικό.",
      "Υποστήριξη φοιτητών, ομάδων λειτουργίας και freelancers με ελαφριά PDF workflows.",
    ],
    useCasesImage: [
      "Προετοιμασία assets για ιστοσελίδες, marketplaces, portfolios και social posts.",
      "Μετατροπή ή βελτιστοποίηση αρχείων πριν το ανέβασμα σε CMS ή ecommerce συστήματα.",
      "Μείωση χειροκίνητης επεξεργασίας σε design και marketing workflows.",
    ],
    useCasesText: [
      "Μορφοποίηση περιεχομένου για δημοσίευση, κοινοποίηση ή συνεργασία.",
      "Μετατροπή δομημένου κειμένου για documentation, development ή content ops.",
      "Επιτάχυνση επαναλαμβανόμενης γραφής και επεξεργασίας.",
    ],
    useCasesOther: [
      "Ολοκληρώστε την εργασία απευθείας στον browser.",
      "Αποφύγετε περιττή εγκατάσταση ή λογισμικό.",
      "Εργαστείτε γρηγορότερα με ένα στοχευμένο workflow.",
    ],
    toolWord: "Εργαλείο",
    featureList: [
      "Δωρεάν χρήση",
      "Χωρίς εγγραφή",
      "Εστίαση στην ιδιωτικότητα",
      "Γρήγορη επεξεργασία",
      "Λειτουργεί στον browser",
    ],
  },
  uk: {
    questionHow: "Як користуватися {tool}?",
    answerHow:
      "Завантажте файл або введіть текст, налаштуйте доступні параметри, натисніть кнопку дії, а потім завантажте або скопіюйте результат. {tool} працює безпосередньо в браузері для кращої приватності та швидкості.",
    questionFree: "{tool} безкоштовний?",
    answerFree:
      "Так. {tool} безкоштовний, не потребує реєстрації та може використовуватися скільки завгодно разів.",
    questionSafe: "Чи безпечні мої дані під час використання {tool}?",
    answerSafe:
      "Так. Більшість обробки виконується безпосередньо у вашому браузері, тому файли залишаються більше під вашим контролем.",
    questionFormats: "Які формати підтримує {tool}?",
    questionInstall: "Чи потрібно щось встановлювати для використання {tool}?",
    answerInstall:
      "Нічого встановлювати не потрібно. {tool} працює прямо у вашому веббраузері.",
    pdfFormats:
      "{tool} підтримує PDF-файли та, залежно від workflow, поширені формати, як-от JPG, PNG, WEBP, Word, Excel або PowerPoint.",
    imageFormats:
      "{tool} підтримує поширені графічні формати, як-от JPG, PNG, WEBP, GIF, BMP, TIFF і SVG залежно від workflow.",
    textFormats:
      "{tool} працює зі звичайним текстом, форматованим текстом, фрагментами коду та іншим текстовим вмістом.",
    otherFormats:
      "{tool} підтримує стандартні формати введення та виведення для цього workflow. Перевірте сторінку інструмента для точної сумісності.",
    overviewPdf:
      "{tool} допомагає працювати з PDF онлайн без додаткового ПЗ. Це зручно для швидких документних задач і браузерної обробки.",
    overviewImage:
      "{tool} допомагає оптимізувати, конвертувати або готувати зображення безпосередньо в браузері. Це зручно для креаторів, ecommerce-команд і власників сайтів.",
    overviewText:
      "{tool} допомагає очищати, перетворювати або аналізувати текст безпосередньо в браузері. Це зручно для авторів, студентів, маркетологів і розробників.",
    overviewOther:
      "{tool} — це спеціалізований браузерний інструмент для швидкого виконання одного workflow.",
    useCasesPdf: [
      "Швидше готувати договори, звіти, робочі таблиці та файли клієнтів.",
      "Виконувати документні задачі без десктопного ПЗ.",
      "Підтримувати студентів, операційні команди та фрилансерів легкими PDF workflow.",
    ],
    useCasesImage: [
      "Готувати матеріали для сайтів, маркетплейсів, портфоліо та соцмереж.",
      "Конвертувати або оптимізувати файли перед завантаженням у CMS чи ecommerce-системи.",
      "Зменшувати ручне редагування в дизайні та маркетингу.",
    ],
    useCasesText: [
      "Форматувати контент для публікації, спільного використання або співпраці.",
      "Перетворювати структурований текст для документації, розробки або content ops.",
      "Прискорювати повторювану роботу з написання та редагування.",
    ],
    useCasesOther: [
      "Виконайте задачу прямо в браузері.",
      "Уникайте зайвого налаштування чи додаткового ПЗ.",
      "Працюйте швидше з фокусованим workflow.",
    ],
    toolWord: "Інструмент",
    featureList: [
      "Безкоштовно",
      "Без реєстрації",
      "З акцентом на приватність",
      "Швидка обробка",
      "Працює в браузері",
    ],
  },
  ru: {
    questionHow: "Как использовать {tool}?",
    answerHow:
      "Загрузите файл или введите текст, настройте доступные параметры, нажмите кнопку действия, а затем скачайте или скопируйте результат. {tool} работает прямо в браузере для лучшей приватности и скорости.",
    questionFree: "{tool} бесплатный?",
    answerFree:
      "Да. {tool} бесплатный, не требует регистрации и может использоваться сколько угодно раз.",
    questionSafe: "Безопасны ли мои данные при использовании {tool}?",
    answerSafe:
      "Да. Большая часть обработки происходит прямо в вашем браузере, поэтому файлы остаются под вашим контролем.",
    questionFormats: "Какие форматы поддерживает {tool}?",
    questionInstall: "Нужно ли что-то устанавливать, чтобы использовать {tool}?",
    answerInstall:
      "Установка не требуется. {tool} работает прямо в вашем веб-браузере.",
    pdfFormats:
      "{tool} поддерживает PDF-файлы и, в зависимости от workflow, популярные форматы вроде JPG, PNG, WEBP, Word, Excel или PowerPoint.",
    imageFormats:
      "{tool} поддерживает популярные графические форматы, такие как JPG, PNG, WEBP, GIF, BMP, TIFF и SVG в зависимости от workflow.",
    textFormats:
      "{tool} работает с обычным текстом, форматированным текстом, фрагментами кода и другим текстовым контентом.",
    otherFormats:
      "{tool} поддерживает стандартные форматы ввода и вывода для этого workflow. Подробную совместимость смотрите на странице инструмента.",
    overviewPdf:
      "{tool} помогает выполнять PDF-задачи онлайн без дополнительного ПО. Это удобно для быстрых документных задач и работы прямо в браузере.",
    overviewImage:
      "{tool} помогает оптимизировать, конвертировать или подготавливать изображения прямо в браузере. Это полезно для креаторов, ecommerce-команд и владельцев сайтов.",
    overviewText:
      "{tool} помогает очищать, преобразовывать или анализировать текст прямо в браузере. Это полезно для авторов, студентов, маркетологов и разработчиков.",
    overviewOther:
      "{tool} — это узкоспециализированная браузерная утилита для быстрого выполнения одного workflow.",
    useCasesPdf: [
      "Быстрее подготавливать договоры, отчеты, таблицы и клиентские файлы.",
      "Выполнять документные задачи без десктопного ПО.",
      "Поддерживать студентов, операционные команды и фрилансеров легкими PDF workflow.",
    ],
    useCasesImage: [
      "Готовить материалы для сайтов, маркетплейсов, портфолио и соцсетей.",
      "Конвертировать или оптимизировать файлы перед загрузкой в CMS или ecommerce-системы.",
      "Снижать объем ручного редактирования в дизайне и маркетинге.",
    ],
    useCasesText: [
      "Форматировать контент для публикации, обмена или совместной работы.",
      "Преобразовывать структурированный текст для документации, разработки или content ops.",
      "Ускорять повторяющуюся работу по написанию и редактированию.",
    ],
    useCasesOther: [
      "Выполняйте задачу прямо в браузере.",
      "Избегайте лишней настройки и дополнительного ПО.",
      "Работайте быстрее с фокусным workflow.",
    ],
    toolWord: "Инструмент",
    featureList: [
      "Бесплатно",
      "Без регистрации",
      "С акцентом на приватность",
      "Быстрая обработка",
      "Работает в браузере",
    ],
  },
  ar: {
    questionHow: "كيف أستخدم {tool}؟",
    answerHow:
      "ارفع ملفك أو أدخل النص، واضبط الخيارات المتاحة، ثم اضغط زر التنفيذ وبعدها نزّل النتيجة أو انسخها. يعمل {tool} مباشرة داخل المتصفح لخصوصية وسرعة أفضل.",
    questionFree: "هل {tool} مجاني؟",
    answerFree:
      "نعم. {tool} مجاني ولا يتطلب تسجيلًا ويمكن استخدامه بقدر ما تحتاج.",
    questionSafe: "هل بياناتي آمنة عند استخدام {tool}؟",
    answerSafe:
      "نعم. تتم معظم المعالجة مباشرة داخل المتصفح، مما يساعد على إبقاء ملفاتك تحت سيطرتك.",
    questionFormats: "ما الصيغ التي يدعمها {tool}؟",
    questionInstall: "هل أحتاج إلى تثبيت أي شيء لاستخدام {tool}؟",
    answerInstall: "لا حاجة إلى التثبيت. يعمل {tool} مباشرة داخل متصفح الويب.",
    pdfFormats:
      "يدعم {tool} ملفات PDF، وبحسب سير العمل يدعم أيضًا صيغًا شائعة مثل JPG وPNG وWEBP وWord وExcel وPowerPoint.",
    imageFormats:
      "يدعم {tool} صيغ الصور الشائعة مثل JPG وPNG وWEBP وGIF وBMP وTIFF وSVG بحسب سير العمل.",
    textFormats:
      "يعمل {tool} مع النص العادي والنص المنسق ومقاطع الكود والمحتوى النصي الآخر.",
    otherFormats:
      "يدعم {tool} صيغ الإدخال والإخراج القياسية لهذا المسار. راجع صفحة الأداة لمعرفة التوافق الدقيق.",
    overviewPdf:
      "يساعد {tool} في تنفيذ مهام PDF عبر الإنترنت دون برامج إضافية. وهو مناسب للمهام السريعة ومعالجة المستندات عبر المتصفح.",
    overviewImage:
      "يساعد {tool} في تحسين الصور أو تحويلها أو تجهيزها مباشرة داخل المتصفح. وهو مناسب للمبدعين وفرق التجارة الإلكترونية وأصحاب المواقع.",
    overviewText:
      "يساعد {tool} في تنظيف النص أو تحويله أو تحليله مباشرة داخل المتصفح. وهو مناسب للكتّاب والطلاب والمسوقين والمطورين.",
    overviewOther:
      "{tool} أداة متخصصة تعمل داخل المتصفح لإنجاز مسار عمل محدد بسرعة.",
    useCasesPdf: [
      "تجهيز العقود والتقارير والجداول وملفات العملاء بسرعة أكبر.",
      "إنجاز مهام المستندات دون برامج سطح مكتب.",
      "دعم الطلاب وفرق العمليات والمستقلين بمسارات PDF خفيفة.",
    ],
    useCasesImage: [
      "تجهيز الأصول للمواقع والمتاجر والملفات التعريفية والمنشورات الاجتماعية.",
      "تحويل الملفات أو تحسينها قبل رفعها إلى أنظمة CMS أو التجارة الإلكترونية.",
      "تقليل العمل اليدوي في مسارات التصميم والتسويق.",
    ],
    useCasesText: [
      "تنسيق المحتوى للنشر أو المشاركة أو التعاون.",
      "تحويل النص المنظم لأغراض التوثيق أو التطوير أو content ops.",
      "تسريع أعمال الكتابة والتحرير المتكررة.",
    ],
    useCasesOther: [
      "أنجز المهمة مباشرة داخل المتصفح.",
      "تجنب الإعداد غير الضروري أو البرامج الإضافية.",
      "اعمل بشكل أسرع مع مسار عمل واضح.",
    ],
    toolWord: "أداة",
    featureList: [
      "مجاني للاستخدام",
      "بدون تسجيل",
      "يركز على الخصوصية",
      "معالجة سريعة",
      "يعمل في المتصفح",
    ],
  },
  ko: {
    questionHow: "{tool}는 어떻게 사용하나요?",
    answerHow:
      "파일을 업로드하거나 텍스트를 입력하고, 사용 가능한 옵션을 조정한 뒤, 실행 버튼을 누르고 결과를 다운로드하거나 복사하세요. {tool}는 더 나은 속도와 개인정보 보호를 위해 브라우저에서 직접 실행됩니다.",
    questionFree: "{tool}는 무료인가요?",
    answerFree:
      "네. {tool}는 무료이며 가입이 필요 없고 필요한 만큼 반복해서 사용할 수 있습니다.",
    questionSafe: "{tool}를 사용할 때 내 데이터는 안전한가요?",
    answerSafe:
      "네. 대부분의 처리가 브라우저에서 직접 이루어지므로 파일을 더 잘 통제할 수 있습니다.",
    questionFormats: "{tool}는 어떤 형식을 지원하나요?",
    questionInstall: "{tool}를 사용하려면 무엇인가 설치해야 하나요?",
    answerInstall: "설치할 필요가 없습니다. {tool}는 웹 브라우저에서 바로 작동합니다.",
    pdfFormats:
      "{tool}는 PDF 파일을 지원하며, workflow에 따라 JPG, PNG, WEBP, Word, Excel, PowerPoint 같은 일반 형식도 지원합니다.",
    imageFormats:
      "{tool}는 workflow에 따라 JPG, PNG, WEBP, GIF, BMP, TIFF, SVG 같은 일반 이미지 형식을 지원합니다.",
    textFormats:
      "{tool}는 일반 텍스트, 서식 있는 텍스트, 코드 스니펫 및 기타 텍스트 기반 콘텐츠와 함께 작동합니다.",
    otherFormats:
      "{tool}는 이 workflow에 필요한 표준 입력 및 출력 형식을 지원합니다. 정확한 호환성은 도구 페이지를 확인하세요.",
    overviewPdf:
      "{tool}는 추가 소프트웨어 없이 온라인에서 PDF 작업을 처리할 수 있도록 도와줍니다. 빠른 문서 작업과 브라우저 기반 처리에 적합합니다.",
    overviewImage:
      "{tool}는 브라우저에서 직접 이미지를 최적화, 변환 또는 준비할 수 있도록 도와줍니다. 크리에이터, 이커머스 팀, 사이트 운영자에게 유용합니다.",
    overviewText:
      "{tool}는 브라우저에서 직접 텍스트를 정리, 변환 또는 분석할 수 있도록 도와줍니다. 작가, 학생, 마케터, 개발자에게 유용합니다.",
    overviewOther:
      "{tool}는 하나의 특정 workflow를 빠르게 완료하도록 설계된 브라우저 기반 유틸리티입니다.",
    useCasesPdf: [
      "계약서, 보고서, 워크시트, 고객 파일을 더 빠르게 준비합니다.",
      "데스크톱 소프트웨어 없이 문서 작업을 완료합니다.",
      "학생, 운영팀, 프리랜서를 가벼운 PDF workflow로 지원합니다.",
    ],
    useCasesImage: [
      "웹사이트, 마켓플레이스, 포트폴리오, 소셜 게시물용 자산을 준비합니다.",
      "CMS나 이커머스 시스템에 업로드하기 전에 파일을 변환하거나 최적화합니다.",
      "디자인 및 마케팅 workflow의 수작업 편집을 줄입니다.",
    ],
    useCasesText: [
      "게시, 공유, 협업을 위한 콘텐츠를 서식화합니다.",
      "문서화, 개발, content ops를 위해 구조화된 텍스트를 변환합니다.",
      "반복적인 작성 및 편집 작업을 더 빠르게 처리합니다.",
    ],
    useCasesOther: [
      "브라우저에서 바로 작업을 완료하세요.",
      "불필요한 설치나 추가 소프트웨어를 피하세요.",
      "집중된 workflow로 더 빠르게 작업하세요.",
    ],
    toolWord: "도구",
    featureList: [
      "무료 사용",
      "회원가입 불필요",
      "개인정보 보호 중심",
      "빠른 처리",
      "브라우저에서 작동",
    ],
  },
  ja: {
    questionHow: "{tool} の使い方は？",
    answerHow:
      "ファイルをアップロードするかテキストを入力し、利用可能な設定を調整して、実行ボタンを押したあと結果をダウンロードまたはコピーします。{tool} はプライバシーと速度のためにブラウザ上で直接動作します。",
    questionFree: "{tool} は無料ですか？",
    answerFree:
      "はい。{tool} は無料で、登録は不要で、必要なだけ何度でも使えます。",
    questionSafe: "{tool} を使うとき、データは安全ですか？",
    answerSafe:
      "はい。処理の大部分はブラウザ内で直接行われるため、ファイルをより自分で管理できます。",
    questionFormats: "{tool} はどの形式に対応していますか？",
    questionInstall: "{tool} を使うために何かインストールが必要ですか？",
    answerInstall: "インストールは不要です。{tool} はそのままブラウザで動作します。",
    pdfFormats:
      "{tool} は PDF ファイルに対応しており、workflow に応じて JPG、PNG、WEBP、Word、Excel、PowerPoint などの一般的な形式にも対応します。",
    imageFormats:
      "{tool} は workflow に応じて JPG、PNG、WEBP、GIF、BMP、TIFF、SVG などの一般的な画像形式に対応します。",
    textFormats:
      "{tool} はプレーンテキスト、整形済みテキスト、コード断片、その他のテキストベースの内容に対応します。",
    otherFormats:
      "{tool} はこの workflow に必要な標準的な入出力形式に対応します。詳しい互換性はツールページをご確認ください。",
    overviewPdf:
      "{tool} は追加ソフトなしで PDF workflow をオンラインで処理できるようにします。素早い文書作業やブラウザベースの処理に適しています。",
    overviewImage:
      "{tool} は画像の最適化、変換、準備をブラウザ上で直接行えるようにします。クリエイター、EC チーム、サイト運営者に役立ちます。",
    overviewText:
      "{tool} はテキストの整理、変換、解析をブラウザ上で直接行えるようにします。ライター、学生、マーケター、開発者に役立ちます。",
    overviewOther:
      "{tool} は特定の workflow を素早く完了するためのブラウザベースの専用ユーティリティです。",
    useCasesPdf: [
      "契約書、レポート、ワークシート、顧客ファイルをより素早く準備する。",
      "デスクトップソフトなしで文書作業を完了する。",
      "学生、運用チーム、フリーランスを軽量な PDF workflow で支援する。",
    ],
    useCasesImage: [
      "Web サイト、マーケットプレイス、ポートフォリオ、SNS 投稿向けの素材を準備する。",
      "CMS や EC システムにアップロードする前にファイルを変換または最適化する。",
      "デザインやマーケティング workflow での手作業の編集を減らす。",
    ],
    useCasesText: [
      "公開、共有、共同作業のためにコンテンツを整える。",
      "ドキュメント、開発、content ops 向けに構造化テキストを変換する。",
      "繰り返し発生する執筆・編集作業を高速化する。",
    ],
    useCasesOther: [
      "ブラウザ上で直接タスクを完了する。",
      "不要な設定や追加ソフトを避ける。",
      "集中した workflow でより速く進める。",
    ],
    toolWord: "ツール",
    featureList: [
      "無料で使える",
      "登録不要",
      "プライバシー重視",
      "高速処理",
      "ブラウザで動作",
    ],
  },
  ro: {
    questionHow: "Cum folosesc {tool}?",
    answerHow:
      "Încarcă fișierul sau introdu textul, ajustează opțiunile disponibile, apasă butonul de acțiune, apoi descarcă sau copiază rezultatul. {tool} rulează direct în browser pentru mai multă confidențialitate și viteză.",
    questionFree: "{tool} este gratuit?",
    answerFree:
      "Da. {tool} este gratuit, nu necesită înregistrare și poate fi folosit ori de câte ori ai nevoie.",
    questionSafe: "Sunt datele mele în siguranță când folosesc {tool}?",
    answerSafe:
      "Da. Cea mai mare parte a procesării are loc direct în browser, ceea ce ajută la păstrarea fișierelor sub controlul tău.",
    questionFormats: "Ce formate acceptă {tool}?",
    questionInstall: "Trebuie să instalez ceva pentru a folosi {tool}?",
    answerInstall:
      "Nu este nevoie de instalare. {tool} funcționează direct în browserul web.",
    pdfFormats:
      "{tool} acceptă fișiere PDF și, în funcție de workflow, formate comune precum JPG, PNG, WEBP, Word, Excel sau PowerPoint.",
    imageFormats:
      "{tool} acceptă formate de imagine comune precum JPG, PNG, WEBP, GIF, BMP, TIFF și SVG, în funcție de workflow.",
    textFormats:
      "{tool} funcționează cu text simplu, text formatat, fragmente de cod și alt conținut bazat pe text.",
    otherFormats:
      "{tool} acceptă formate standard de intrare și ieșire pentru acest workflow. Verifică pagina uneltei pentru compatibilitatea exactă.",
    overviewPdf:
      "{tool} ajută la gestionarea workflow-urilor PDF online fără software suplimentar. Este util pentru sarcini rapide cu documente și procesare în browser.",
    overviewImage:
      "{tool} ajută la optimizarea, conversia sau pregătirea imaginilor direct în browser. Este util pentru creatori, echipe ecommerce și proprietari de site-uri.",
    overviewText:
      "{tool} ajută la curățarea, transformarea sau analizarea textului direct în browser. Este util pentru autori, studenți, marketeri și dezvoltatori.",
    overviewOther:
      "{tool} este un utilitar concentrat în browser, creat pentru a finaliza rapid un workflow specific.",
    useCasesPdf: [
      "Pregătirea mai rapidă a contractelor, rapoartelor, foilor de lucru și fișierelor pentru clienți.",
      "Rezolvarea sarcinilor documentare fără software desktop.",
      "Sprijinirea studenților, echipelor operaționale și freelancerilor cu workflow-uri PDF ușoare.",
    ],
    useCasesImage: [
      "Pregătirea materialelor pentru site-uri, marketplace-uri, portofolii și postări sociale.",
      "Conversia sau optimizarea fișierelor înainte de încărcarea în CMS sau sisteme ecommerce.",
      "Reducerea editării manuale în workflow-urile de design și marketing.",
    ],
    useCasesText: [
      "Formatarea conținutului pentru publicare, distribuire sau colaborare.",
      "Transformarea textului structurat pentru documentație, development sau content ops.",
      "Accelerarea muncii repetitive de scriere și editare.",
    ],
    useCasesOther: [
      "Finalizează sarcina direct în browser.",
      "Evită configurarea sau software-ul inutil.",
      "Lucrează mai repede cu un workflow concentrat.",
    ],
    toolWord: "Unealtă",
    featureList: [
      "Gratuit",
      "Fără înregistrare",
      "Axat pe confidențialitate",
      "Procesare rapidă",
      "Funcționează în browser",
    ],
  },
};

function fill(template: string, tool: string) {
  return template.replaceAll("{tool}", tool);
}

export function getSeoCopy(locale: Locale) {
  return seoCopy[locale] ?? seoCopy.en;
}

export function fillSeoText(template: string, tool: string) {
  return fill(template, tool);
}
